import { lazy, Suspense, useEffect } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
// auth
import { AuthGuard, RoleBasedGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';
import CourseGroupListPage from 'src/pages/dashboard/course-group/list';
import { useAuthContext } from 'src/auth/hooks';

// ----------------------------------------------------------------------

// OVERVIEW
const IndexPage = lazy(() => import('src/pages/dashboard/app'));
const OverviewEcommercePage = lazy(() => import('src/pages/dashboard/ecommerce'));
const OverviewAnalyticsPage = lazy(() => import('src/pages/dashboard/analytics'));
const OverviewBankingPage = lazy(() => import('src/pages/dashboard/banking'));
const OverviewBookingPage = lazy(() => import('src/pages/dashboard/booking'));
const OverviewFilePage = lazy(() => import('src/pages/dashboard/file'));
// PRODUCT
const ProductDetailsPage = lazy(() => import('src/pages/dashboard/product/details'));
const ProductListPage = lazy(() => import('src/pages/dashboard/product/list'));
const ProductCreatePage = lazy(() => import('src/pages/dashboard/product/new'));
const ProductEditPage = lazy(() => import('src/pages/dashboard/product/edit'));
// ORDER
const OrderListPage = lazy(() => import('src/pages/dashboard/order/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/order/details'));
// OUTSIDER
const OutsiderProfilePage = lazy(() => import('src/pages/dashboard/outsider/profile'));
const OutsiderListPage = lazy(() => import('src/pages/dashboard/outsider/list'));
const OutsiderCreatePage = lazy(() => import('src/pages/dashboard/outsider/new'));
const OutsiderEditPage = lazy(() => import('src/pages/dashboard/outsider/edit'));
const OutsiderLog = lazy(() => import('src/pages/dashboard/outsider/log'));
// INVOICE
const InvoiceListPage = lazy(() => import('src/pages/dashboard/invoice/list'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoice/details'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoice/new'));
const InvoiceEditPage = lazy(() => import('src/pages/dashboard/invoice/edit'));
// USER
const UserNonAcademicProfilePage = lazy(() => import('src/pages/dashboard/user/profile-nonacademic'));
const UserProfilePage = lazy(() => import('src/pages/dashboard/user/profile'));
const UserCardsPage = lazy(() => import('src/pages/dashboard/user/cards'));
const UserListPage = lazy(() => import('src/pages/dashboard/user/list'));
const UserAccountPage = lazy(() => import('src/pages/dashboard/user/account'));
const UserCreatePage = lazy(() => import('src/pages/dashboard/user/new'));
const UserCreatePage1 = lazy(() => import('src/pages/dashboard/user/new-nonacademic'));
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
const UserNonAcademicEditPage = lazy(() => import('src/pages/dashboard/user/edit-nonacademic'));
const UserLog = lazy(() => import('src/pages/dashboard/user/log'));
// BATCH
const BatchProfilePage = lazy(() => import('src/pages/dashboard/batch/profile'));
const BatchCardsPage = lazy(() => import('src/pages/dashboard/batch/cards'));
const BatchListPage = lazy(() => import('src/pages/dashboard/batch/list'));
const BatchAccountPage = lazy(() => import('src/pages/dashboard/batch/account'));
const BatchCreatePage = lazy(() => import('src/pages/dashboard/batch/new'));
const BatchEditPage = lazy(() => import('src/pages/dashboard/batch/edit'));
const BatchViewPage = lazy(() => import('src/pages/dashboard/batch/view'));
const BatchLog = lazy(() => import('src/pages/dashboard/batch/logs'));

// TEACHER
const TeacherAssignPage = lazy(() => import('src/pages/dashboard/teacher/list'));
const TeacherTransferPage = lazy(() => import('src/pages/dashboard/teacher/transfer'));
const AllocateTeacher = lazy(() => import('src/pages/dashboard/teacher/allocate'));
const UnallocateTeacher = lazy(() => import('src/pages/dashboard/teacher/unallocate'));
const TeacherProfilePage = lazy(() => import('src/pages/dashboard/teacher/profile'));
const SubjectWiseTeaherPage = lazy(() => import('src/pages/dashboard/teacher/subject-wise-teacher'));
const CourseBatchViewPage = lazy(() => import('src/pages/dashboard/teacher/batch'));
const BatchTeacherSubjectPage = lazy(() => import('src/pages/dashboard/teacher/batch-teacher-subject'));
const TeacherLog = lazy(() => import('src/pages/dashboard/teacher/log'));
const OnlineTeacherSubjectPage = lazy(() => import('src/pages/dashboard/teacher/online-teacher-subject'));
// BUILDING-STRUCTURE
const BuildingStructureCreatePage = lazy(() => import('src/pages/dashboard/building-structure/new'));
const BuildingStructureEditPage = lazy(() => import('src/pages/dashboard/building-structure/edit'));
const BuildingStructureListPage = lazy(() => import('src/pages/dashboard/building-structure/list'));
// STUDENT
const StudentProfilePage = lazy(() => import('src/pages/dashboard/student/profile'));
const StudentCardsPage = lazy(() => import('src/pages/dashboard/student/cards'));
const StudentListPage = lazy(() => import('src/pages/dashboard/student/list'));
const StudentAccountPage = lazy(() => import('src/pages/dashboard/student/account'));
const StudentCreatePage = lazy(() => import('src/pages/dashboard/student/new'));
const StudentCreateWithoutStepsPage = lazy(() => import('src/pages/dashboard/student/new-without-steps'));
const StudentApprovalsPage = lazy(() => import('src/pages/dashboard/student/approvals'));
const StudentEditPage = lazy(() => import('src/pages/dashboard/student/edit'));
const StudentRegisterPage = lazy(() => import('src/pages/dashboard/student/register'));
const StudentBulkPage = lazy(() => import('src/pages/dashboard/student/bulk-upload'));
const StudentLog = lazy(() => import('src/pages/dashboard/student/logs'));
const SessionViewPage = lazy(() => import('src/pages/dashboard/session/list'));
// REGISTERED STUDENT LIST
const RegisterStudentListPage = lazy(() => import('src/pages/dashboard/register-student/list'));


// STUDENT ONLINE ENROLLMENT
const OnlineCourseEnrollPage = lazy(() => import('src/pages/dashboard/online-enroll/new'));

// COURSES
const CourseProfilePage = lazy(() => import('src/pages/dashboard/courses/profile'));
const CourseCardsPage = lazy(() => import('src/pages/dashboard/courses/cards'));
const CourseListPage = lazy(() => import('src/pages/dashboard/courses/list'));
const CourseAccountPage = lazy(() => import('src/pages/dashboard/courses/account'));
const CourseCreatePage = lazy(() => import('src/pages/dashboard/courses/new'));
const CourseEditPage = lazy(() => import('src/pages/dashboard/courses/edit'));
const CourseConfigurePage = lazy(() => import('src/pages/dashboard/courses/configure'));
const CourseMaterialPage = lazy(() => import('src/pages/dashboard/courses/material'));
const CourseInstallmentCalculatorPage = lazy(() => import('src/pages/dashboard/courses/installment-calculator'));
const CourseOnlineConfigurePage = lazy(() => import('src/pages/dashboard/courses/online'));
const CourseOfflineConfigurePage = lazy(() => import('src/pages/dashboard/courses/offline'));
const CourseHybridConfigurePage = lazy(() => import('src/pages/dashboard/courses/hybrid'));
const CourseGroupProfilePage = lazy(() => import('src/pages/dashboard/course-group/course-group-profile'));
const CoursesLog = lazy(() => import('src/pages/dashboard/courses/logs'));

// HEIGHT EXAM
const HeightExamProfilePage = lazy(() => import('src/pages/dashboard/height-exam/profile'));
const HeightExamListPage = lazy(() => import('src/pages/dashboard/height-exam/list'));
const HeightExamCreatePage = lazy(() => import('src/pages/dashboard/height-exam/new'));
const HeightExamEditPage = lazy(() => import('src/pages/dashboard/height-exam/edit'));
const LinkTalentHuntExamsPage = lazy(() => import('src/pages/dashboard/talent-hunt/link-exams'));
const TalentHuntAdmitCardInstructionsPage = lazy(() => import('src/pages/dashboard/talent-hunt/admit-card-instructions'));
const TalentHuntExamProfilePage = lazy(() => import('src/pages/dashboard/height-student/talent-hunt-profile'));
const HeightExamAnalyticsProfilePage = lazy(() => import('src/pages/dashboard/height-student/analytics'));
const TalentHuntResultListPage = lazy(() => import('src/pages/dashboard/height-student/result-list'));
const TalentHuntResultGroupProfilePage = lazy(() => import('src/pages/dashboard/height-student/result-group-profile'));
const TalentHuntResultGroupStudentProfilePage = lazy(() => import('src/pages/dashboard/height-student/result-group-student-profile'));
const TalentHuntStudentsListPage = lazy(() => import('src/pages/dashboard/height-student/students-list'));
// FEE
const FeeProfilePage = lazy(() => import('src/pages/dashboard/fee/profile'));
const FeeCardsPage = lazy(() => import('src/pages/dashboard/fee/cards'));
const FeeListPage = lazy(() => import('src/pages/dashboard/fee/list'));
const FeeCreatePage = lazy(() => import('src/pages/dashboard/fee/new'));
const FeeEditPage = lazy(() => import('src/pages/dashboard/fee/edit'));
const FeeCollectPage = lazy(() => import('src/pages/dashboard/fee/collect'));

// CHEQUE-APPROVAL
const ChequeApprovalListPage = lazy(() => import('src/pages/dashboard/cheque-approval/list'));
//REGISTER
const RegisterListPage = lazy(() => import('src/pages/dashboard/register/list'));
const RegisterProfilePage = lazy(() => import('src/pages/dashboard/register/profile'));
// BLOG
const BlogPostsPage = lazy(() => import('src/pages/dashboard/post/list'));
const BlogPostPage = lazy(() => import('src/pages/dashboard/post/details'));
const BlogNewPostPage = lazy(() => import('src/pages/dashboard/post/new'));
const BlogEditPostPage = lazy(() => import('src/pages/dashboard/post/edit'));
// JOB
const JobDetailsPage = lazy(() => import('src/pages/dashboard/job/details'));
const JobListPage = lazy(() => import('src/pages/dashboard/job/list'));
const JobCreatePage = lazy(() => import('src/pages/dashboard/job/new'));
const JobEditPage = lazy(() => import('src/pages/dashboard/job/edit'));
// TOUR
const TourDetailsPage = lazy(() => import('src/pages/dashboard/tour/details'));
const TourListPage = lazy(() => import('src/pages/dashboard/tour/list'));
const TourCreatePage = lazy(() => import('src/pages/dashboard/tour/new'));
const TourEditPage = lazy(() => import('src/pages/dashboard/tour/edit'));
// FILE MANAGER
const FileManagerPage = lazy(() => import('src/pages/dashboard/file-manager'));
// APP
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const MailPage = lazy(() => import('src/pages/dashboard/mail'));
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
const KanbanPage = lazy(() => import('src/pages/dashboard/kanban'));
// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));
// BLANK PAGE
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));
const LearningDashboardPage = lazy(() => import('src/pages/dashboard/mylearning/learning-dashboard'));
const PlayerPage = lazy(() => import('src/pages/dashboard/mylearning/player'));
const CoursesViewPage = lazy(() => import('src/pages/dashboard/mylearning/courses-view'));
const CourseDetailViewPage = lazy(() => import('src/pages/dashboard/mylearning/course-detail-view'));
// EXAMINATION
const ExaminationListPage = lazy(() => import('src/pages/dashboard/examination/list'));
const ExaminationCreatePage = lazy(() => import('src/pages/dashboard/examination/new'));
const ExaminationEditPage = lazy(() => import('src/pages/dashboard/examination/edit'));
const ExaminationProfilePage = lazy(() => import('src/pages/dashboard/examination/profile'));
const ExaminationRankListPage = lazy(() => import('src/pages/dashboard/examination/rank-list'));

// PATTREN
const PattrenListPage = lazy(() => import('src/pages/dashboard/pattren/list'));
const PattrenCreatePage = lazy(() => import('src/pages/dashboard/pattren/new'));
const PattrenEditPage = lazy(() => import('src/pages/dashboard/pattren/edit'));
// OFFLINE-EXAM
const OfflineExamListPage = lazy(() => import('src/pages/dashboard/offline-exam/list'));
const OfflineExamCreatePage = lazy(() => import('src/pages/dashboard/offline-exam/new'));
const OfflineExamEditPage = lazy(() => import('src/pages/dashboard/offline-exam/edit'));
// EXAM LOG
const ExamLogListPage = lazy(() => import('src/pages/dashboard/exam-log/list'));
// LMS
const LmsViewPage = lazy(() => import('src/pages/dashboard/lms/view'));
// STUDENT EXAMINATION
const StudentExaminationListPage = lazy(() => import('src/pages/dashboard/tests/list'));
// STUDENT PROFILE  
const StudentProfile = lazy(() => import('src/pages/dashboard/student-profile/profile'));
// SECHDULE 
const SchedulePage = lazy(() => import('src/pages/dashboard/schedule/list'));
// EXAM RESULTS 
const StudentExaminationResultListPage = lazy(() => import('src/pages/dashboard/test-results/list'));
const TestResultProfilePage = lazy(() => import('src/pages/dashboard/test-results/profile'));
// ATTENDANCE-ADMIN
const AttendanceAdminPage = lazy(() => import('src/pages/dashboard/attendance-admin/list'));
const AttendanceAdminEditPage = lazy(() => import('src/pages/dashboard/attendance-admin/edit'));
const AttendanceAdminCreatePage = lazy(() => import('src/pages/dashboard/attendance-admin/new'));

// ALL PAYMENTS
const AllPaymentListPage = lazy(() => import('src/pages/dashboard/allpayment/list'));

// CASHREGISTER
const CashRegisterListPage = lazy(() => import('src/pages/dashboard/cashregister/list'));
const CashRegisterOpenPage = lazy(() => import('src/pages/dashboard/cashregister/openview'));
const CashRegisterClosePage = lazy(() => import('src/pages/dashboard/cashregister/closeview'));
const CashRegisterReconciliationPage = lazy(() => import('src/pages/dashboard/cashregister/reconciliation'));
const CashRegisterLog = lazy(() => import('src/pages/dashboard/cashregister/logs'));
const CashRegisterApprovalsPage = lazy(() => import('src/pages/dashboard/cashregister/approvals'));
const CashRegisterRequestsPage = lazy(() => import('src/pages/dashboard/cashregister/requests'));

// BATCH-GROUP
const BatchGroupListPage = lazy(() => import('src/pages/dashboard/batch-group/list'));
const BatchGroupCreatePage = lazy(() => import('src/pages/dashboard/batch-group/new'));
const BatchGroupEditPage = lazy(() => import('src/pages/dashboard/batch-group/edit'));
// NON ACADEMIC ROLE
const NonAcademicPermissionDeniedPage = lazy(() => import('src/pages/dashboard/non-academic-permission'));
// DAT CONFIGURATION
const DatConfigurationPage = lazy(() => import('src/pages/dashboard/dat'));
// QUESTION-DIFFICULTY-CONFIGURATION
const QuestionDifficultyConfigurationPage = lazy(() => import('src/pages/dashboard/question-difficulty-configuration'));
// ROLE
const RolePage = lazy(() => import('src/pages/dashboard/role'));
// CENTER
const CenterPage = lazy(() => import('src/pages/dashboard/center'));
// SCHOOL
const SchoolPage = lazy(() => import('src/pages/dashboard/school'));
// TAG
const TagPage = lazy(() => import('src/pages/dashboard/tag'));
// TAG
const OrganizationPage = lazy(() => import('src/pages/dashboard/organization'));
// SUBJECT
const SubjectPage = lazy(() => import('src/pages/dashboard/subject'));
// CLASSES
const ClassesPage = lazy(() => import('src/pages/dashboard/classes'));
// TARGET
const TargetPage = lazy(() => import('src/pages/dashboard/target'));
// TAX
const TaxPage = lazy(() => import('src/pages/dashboard/tax'));
// Discount
const DiscountPage = lazy(() => import('src/pages/dashboard/discount'));
// Pattern
const PatternPage = lazy(() => import('src/pages/dashboard/pattern'));
const PattrenDuplicatePage = lazy(() => import('src/pages/dashboard/pattren/duplicate'));
// ATTENDANCE
const AttendancePage = lazy(() => import('src/pages/dashboard/attendance'));
// PARENT FEE LIST
const ParentFeeListPage = lazy(() => import('src/pages/dashboard/fee-parent/list'));
// STUDENT FEE LIST
const StudentFeeListPage = lazy(() => import('src/pages/dashboard/fee-student/list'));
// QUESTION BANK
const QuestionBankListPage = lazy(() => import('src/pages/dashboard/questionbank/list'));
const QuestionBankCreatePage = lazy(() => import('src/pages/dashboard/questionbank/new'));
const QuestionBankEditPage = lazy(() => import('src/pages/dashboard/questionbank/edit'));
const QuestionBankLog = lazy(() => import('src/pages/dashboard/questionbank/logs'));

// HOSTEL
const HostelListPage = lazy(() => import('src/pages/dashboard/hostel/list'));
const HostelCreatePage = lazy(() => import('src/pages/dashboard/hostel/new'));
const HostelAllocationPage = lazy(() => import('src/pages/dashboard/hostel/allocation'));
const HostelAllocationEditPage = lazy(() => import('src/pages/dashboard/hostel/allocation-edit'));
const HostelAllocationListPage = lazy(() => import('src/pages/dashboard/hostel/allocation-list'));
const HostelAllocationDetailPage = lazy(() => import('src/pages/dashboard/hostel/allocation-detail'));
const HostelAgreementPage = lazy(() => import('src/pages/dashboard/hostel/agreement'));
const HostelAllocationOutsiderDetailPage = lazy(() => import('src/pages/dashboard/hostel/allocation-outsider-detail'));
const HostelEditPage = lazy(() => import('src/pages/dashboard/hostel/edit'));
const HostelProfilePage = lazy(() => import('src/pages/dashboard/hostel/profile'));
const HostelMessFeeCreatePage = lazy(() => import('src/pages/dashboard/hostel/collect'));
const HostelAllocationLog = lazy(() => import('src/pages/dashboard/hostel/allocation-logs'));
const HostelWardenLog = lazy(() => import('src/pages/dashboard/hostel/warden-logs'));
const HostelLog = lazy(() => import('src/pages/dashboard/hostel/hostel-logs'));
const HostelCampusListPage = lazy(() => import('src/pages/dashboard/hostel/campus-list'));
const HostelListRoomPage = lazy(() => import('src/pages/dashboard/hostel/listroom'));
const HostelVacateRefundListPage = lazy(() => import('src/pages/dashboard/hostel/hostel-vacate-refund-list'));
const HostelVacatedBedListPage = lazy(() => import('src/pages/dashboard/hostel/hostel-vacated-bed-list'));
const HostelTextPage = lazy(() => import('src/pages/dashboard/hostel/hostel-text'));
// HOSTEL-MASTER
const HostelMasterPage = lazy(() => import('src/pages/dashboard/hostel-master'));
// TASK
const TaskListPage = lazy(() => import('src/pages/dashboard/task/list'));
// REPORT
const CashRegisterReportPage = lazy(() => import('src/pages/dashboard/reports/report'));
// STUDENT BALANCE REPORT
const StudentBalanceReportPage = lazy(() => import('src/pages/dashboard/reports/student-balance-report'));
// HOSTEL MESS BALANCE REPORT
const HostelMessBalanceReportPage = lazy(() => import('src/pages/dashboard/reports/hostel-mess-balance-report'));
// ADDMISSION SUMMARY REPORT
const AddmissionSummaryReportPage = lazy(() => import('src/pages/dashboard/reports/addmission-summary-report'));
// BATCH TOPPER REPORT
const BatchTopperReportPage = lazy(() => import('src/pages/dashboard/reports/batch-topper'));
const TestResultReportPage = lazy(() => import('src/pages/dashboard/reports/test-result-report'));
const QuizSubjectMarksReportPage = lazy(() => import('src/pages/dashboard/reports/quiz-subject-marks-report'));

// QUESTION WISE ANALYSIS REPORT
const QuesWiseAnalysisReportPage = lazy(() => import('src/pages/dashboard/reports/question-wise-analysis-report'));

// STUDENT TEST ANALYSIS REPORT
const StudentTestAnalysisReportPage = lazy(() => import('src/pages/dashboard/reports/student-test-analysis-report'));
// STUDENT QUESTION WISE ANALYSIS REPORT
const StudentQuesWiseAnalysisReportPage = lazy(() => import('src/pages/dashboard/reports/student-question-wise-analysis-report'));
// STUDENT REPORT CARD
const StudentReportCardPage = lazy(() => import('src/pages/dashboard/reports/student-report-card-report'));

// SYLLABUS
const SyllabusCreatePage = lazy(() => import('src/pages/dashboard/syllabus/new'));
const SyllabusListPage = lazy(() => import('src/pages/dashboard/syllabus/list'));
const SyllabusEditPage = lazy(() => import('src/pages/dashboard/syllabus/edit'));
const SyllabusProfilePage = lazy(() => import('src/pages/dashboard/syllabus/profile'));
const SyllabusLog = lazy(() => import('src/pages/dashboard/syllabus/logs'));

//HEIGHT STUDENT
const HeightRegistrationListPage = lazy(() => import('src/pages/dashboard/height-student/list'));
const HeightRegistrationCreatePage = lazy(() => import('src/pages/dashboard/height-student/create'));
const HeightRegistrationEditPage = lazy(() => import('src/pages/dashboard/height-student/edit'));
const HeightStudentExamProfilePage = lazy(() => import('src/pages/dashboard/height-student/profile'));
const TalentHuntExamCreatePage = lazy(() => import('src/pages/dashboard/height-student/talent-hunt-create'));
const SchoolProfilePage = lazy(() => import('src/pages/dashboard/school-profile'));
const CenterProfilePage = lazy(() => import('src/pages/dashboard/center-profile'));



//// 
const TemplatePage = lazy(() => import('src/pages/dashboard/template'));
const SmsTemplatePage = lazy(() => import('src/pages/dashboard/sms-template'));
const EmailTemplatePage = lazy(() => import('src/pages/dashboard/email-template'));
const WhatsAppTemplatePage = lazy(() => import('src/pages/dashboard/whatsapp-template'));
// 
const CreateCommunicationPage = lazy(() => import('src/pages/dashboard/communication/create'));
const SenderCredentialsPage = lazy(() => import('src/pages/dashboard/communication/sender-credentials'));
const SmsCategoryPage = lazy(() => import('src/pages/dashboard/communication/sms-category'));
const LinkedTemplatePage = lazy(() => import('src/pages/dashboard/communication/linked-template'));
const CommunicationListPage = lazy(() => import('src/pages/dashboard/communication-logs'));
const LoginLogListPage = lazy(() => import('src/pages/dashboard/login-logs'));
const PaymentAmountUpdateLogListPage = lazy(() => import('src/pages/dashboard/payment-amount-update-logs'));

const QuestionBankProfilePage = lazy(() => import('src/pages/dashboard/questionbank/profile'));
const QuestionBankAllocationPage = lazy(() => import('src/pages/dashboard/questionbank/allocation'));

const QRPage = lazy(() => import('src/pages/dashboard/QR'));
//Time Table
const TimeTableViewPage = lazy(() => import('src/pages/dashboard/timetable/timetableviewpage'));
const TimeTableSlotsPage = lazy(() => import('src/pages/dashboard/timetable/timetableslotview'));
// 
const BankPage = lazy(() => import('src/pages/dashboard/bank'));
// REFUND
const RefundListPage = lazy(() => import('src/pages/dashboard/refund/list'));
const RefundCreatePage = lazy(() => import('src/pages/dashboard/refund/create'));
// GST REPORT
const GSTReportPage = lazy(() => import('src/pages/dashboard/reports/gst-report'));
// DAILY COLLECTION REPORT
const DailyCollectionReportPage = lazy(() => import('src/pages/dashboard/reports/daily-collection-report'));

// ----------------------------------------------------------------------
const AuthContent = () => {
  const { user } = useAuthContext();

  return <Outlet />;
};

// ----------------------------------------------------------------------
export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <RoleBasedGuard roles={['admin', 'academic', 'non_academic']} hasContent>
          <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
              <AuthContent />
            </Suspense>
          </DashboardLayout>
        </RoleBasedGuard>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      { path: 'ecommerce', element: <OverviewEcommercePage /> },
      { path: 'analytics', element: <OverviewAnalyticsPage /> },
      { path: 'banking', element: <OverviewBankingPage /> },
      { path: 'booking', element: <OverviewBookingPage /> },
      { path: 'file', element: <OverviewFilePage /> },
      {
        path: 'user',
        children: [
          { element: <UserProfilePage />, index: true },
          { path: ':id/profile/academic', element: <UserProfilePage /> },
          { path: ':id/profile/nonacademic', element: <UserNonAcademicProfilePage /> },
          { path: 'cards', element: <UserCardsPage /> },
          { path: 'list', element: <UserListPage /> },
          { path: 'new', element: <UserCreatePage /> },
          { path: 'new-non-academic', element: <UserCreatePage1 /> },
          { path: ':id/edit', element: <UserEditPage /> },
          { path: 'non-academic/:id/edit', element: <UserNonAcademicEditPage /> },
          { path: 'account', element: <UserAccountPage /> },
          { path: 'logs', element: <UserLog /> },
          
        ],
      },
      { path: 'template', element: <TemplatePage /> },
      { path: 'sms-template', element: <SmsTemplatePage /> },
      {
        path: 'communication',
        children: [
          { element: <Navigate to="/dashboard/communication/create" replace />, index: true },
          { path: 'create', element: <CreateCommunicationPage /> },
          { path: 'sender-credentials', element: <SenderCredentialsPage /> },
          { path: 'sms-template', element: <SmsTemplatePage /> },
          { path: 'email-template', element: <EmailTemplatePage /> },
          { path: 'whatsapp-template', element: <WhatsAppTemplatePage /> },
          { path: 'sms-category', element: <SmsCategoryPage /> },
          { path: 'linked-template', element: <LinkedTemplatePage /> },
          { path: 'logs', element: <CommunicationListPage /> },
        ],
      },
      { path: 'linked-template', element: <LinkedTemplatePage /> },
      { path: 'communication-logs', element: <CommunicationListPage /> },
      {
        path: 'login-logs',
        element: (
          <RoleBasedGuard roles={['admin']} hasContent>
            <LoginLogListPage />
          </RoleBasedGuard>
        ),
      },
      {
        path: 'payment-amount-update-logs',
        element: (
          <RoleBasedGuard roles={['admin']} hasContent>
            <PaymentAmountUpdateLogListPage />
          </RoleBasedGuard>
        ),
      },

      {
        path: 'student',
        children: [
          { element: <StudentProfilePage />, index: true },
          { path: ':id/profile', element: <StudentProfilePage /> },
          { path: 'cards', element: <StudentCardsPage /> },
          { path: 'list', element: <StudentListPage /> },
          { path: 'new', element: <StudentCreatePage /> },
          { path: 'new-without-steps', element: <StudentCreateWithoutStepsPage /> },
          { path: 'approvals', element: <StudentApprovalsPage /> },
          { path: 'approvals/:id', element: <StudentApprovalsPage /> },
          { path: ':id/edit', element: <StudentEditPage /> },
          { path: 'account', element: <StudentAccountPage /> },
          { path: 'register', element: <StudentRegisterPage /> },
          { path: 'bulkupload', element: <StudentBulkPage /> },
          { path: 'logs', element: <StudentLog /> },
          
        ],
      },
      {
        path: 'registerlist',
        children: [
          { element: <RegisterStudentListPage />, index: true },
          { path: 'list', element: <RegisterStudentListPage /> },
  
          
        ],
      },
      {
        path: 'outsider',
        children: [
          { element: <OutsiderListPage />, index: true },
          { path: ':id/profile', element: <OutsiderProfilePage /> },
          { path: 'list', element: <OutsiderListPage /> },
          { path: 'new', element: <OutsiderCreatePage /> },
          { path: 'logs', element: <OutsiderLog /> },
          { path: ':id/edit', element: <OutsiderEditPage /> },
        ],
      },
            {
        path: 'onlineenroll',
        children: [
          
          { path: ':id/new', element: <OnlineCourseEnrollPage /> },
        ],
      },
      {
        path: 'batch',
        children: [
          { element: <BatchProfilePage />, index: true },
          { path: ':id/profile', element: <BatchProfilePage /> },
          { path: 'cards', element: <BatchCardsPage /> },
          { path: 'list', element: <BatchListPage /> },
          { path: 'new', element: <BatchCreatePage /> },
          { path: ':id/edit', element: <BatchEditPage /> },
          { path: 'account', element: <BatchAccountPage /> },
          { path: 'view', element: <BatchViewPage /> },
          { path: 'logs', element: <BatchLog /> },

        ],
      },
      {
        path: 'teacher',
        children: [
          { element: <TeacherProfilePage />, index: true },
          { path: 'profile', element: <TeacherProfilePage /> },
          { path: 'assign', element: <TeacherAssignPage /> },
          { path: 'transfer', element: <TeacherTransferPage /> },
          { path: 'allocate', element: <AllocateTeacher /> },
          { path: 'unallocate', element: <UnallocateTeacher /> },
          { path: ':id/allocate', element: <SubjectWiseTeaherPage /> },
          { path: ':id/batch', element: <CourseBatchViewPage /> },
          { path: ':id/batchsubject', element: <BatchTeacherSubjectPage /> },
          { path: 'log', element: <TeacherLog /> },
          { path: ':id/subject', element: <OnlineTeacherSubjectPage /> },
        ],
      },
      {
        path: 'building',
        children: [
          { element: <BuildingStructureListPage />, index: true },
          { path: 'list', element: <BuildingStructureListPage /> },
          { path: 'new', element: <BuildingStructureCreatePage /> },
          { path: ':id/edit', element: <BuildingStructureEditPage /> },
        ],
      },
      {
        path: 'product',
        children: [
          { element: <ProductListPage />, index: true },
          { path: 'list', element: <ProductListPage /> },
          { path: ':id', element: <ProductDetailsPage /> },
          { path: 'new', element: <ProductCreatePage /> },
          { path: ':id/edit', element: <ProductEditPage /> },
        ],
      },
      {
        path: 'batchgroup',
        children: [
          { path: 'list', element: <BatchGroupListPage /> },
          { path: 'new', element: <BatchGroupCreatePage /> },
          { path: ':id/edit', element: <BatchGroupEditPage /> },

        ],
      },
      {
        path: 'height-student',
        children: [
          { path: 'list', element: <HeightRegistrationListPage /> },
          { path: 'new', element: <HeightRegistrationCreatePage /> },
          { path: ':id/edit', element: <HeightRegistrationEditPage /> },
          { path: ':id/profile', element: <HeightStudentExamProfilePage /> },
          { path: 'talent-hunt-exam', element: <TalentHuntExamCreatePage /> },
        ],
      },
      {
        path: 'order',
        children: [
          { element: <OrderListPage />, index: true },
          { path: 'list', element: <OrderListPage /> },
          { path: ':id', element: <OrderDetailsPage /> },
        ],
      },
      {
        path: 'mylearning',
        children: [
          { element: <LearningDashboardPage />, index: true },
          { path: 'player/:courseId', element: <PlayerPage /> },
          { path: 'courses-view', element: <CoursesViewPage /> },
          { path: 'courses-view/:courseId', element: <CourseDetailViewPage /> },
        ],
      },
      {
        path: 'lms',
        children: [{ element: <LmsViewPage />, index: true }],
      },
      {
        path: 'studentexamination',
        children: [
          { element: <StudentExaminationListPage />, index: true },
        ],
      },
      {
        path: 'studentprofile',
        children: [{ element: < StudentProfile />, index: true }],
      },
      {
        path: 'schedule',
        children: [{ element: <SchedulePage />, index: true }],
      },
      {
        path: 'examinationresult',
        children: [
          { element: <StudentExaminationResultListPage />, index: true },
          { path: ':id/profile', element: <TestResultProfilePage /> },
          { path: 'profile', element: <TestResultProfilePage /> },
        ],
      },
      {
        path: 'test-results',
        children: [
          { element: <StudentExaminationResultListPage />, index: true },
          { path: ':id/profile', element: <TestResultProfilePage /> },
          { path: 'profile', element: <TestResultProfilePage /> },
        ],
      },
      {
        path: 'cashregister',
        children: [
          { element: <CashRegisterListPage />, index: true },
          { path: 'list', element: <CashRegisterListPage /> },
          { path: 'openview', element: <CashRegisterOpenPage /> },
          { path: 'closeview', element: <CashRegisterClosePage /> },
          { path: 'reconciliation', element: <CashRegisterReconciliationPage /> },
          { path: 'logs', element: <CashRegisterLog /> },
          { path: 'approvals', element: <CashRegisterApprovalsPage /> },
          { path: 'requests', element: <CashRegisterRequestsPage /> },
        ],
      },
        {
        path: 'timetable',
        children: [
          { element: <TimeTableViewPage />, index: true },
          { path: 'list', element: <TimeTableViewPage /> },
          { path: 'slots', element: <TimeTableSlotsPage /> },

       
          
        ],
      },
      {
        path: 'invoice',
        children: [
          { element: <InvoiceListPage />, index: true },
          { path: 'list', element: <InvoiceListPage /> },
          { path: ':id', element: <InvoiceDetailsPage /> },
          { path: ':id/edit', element: <InvoiceEditPage /> },
          { path: 'new', element: <InvoiceCreatePage /> },
        ],
      },
      {
        path: 'gstreport',
        children: [
          { element: <GSTReportPage />, index: true },
          { path: 'list', element: <GSTReportPage /> },
        ],
      },
      {
        path: 'dailycollectionreport',
        children: [
          { element: <DailyCollectionReportPage />, index: true },
          { path: 'list', element: <DailyCollectionReportPage /> },
        ],
      },
      {
        path: 'post',
        children: [
          { element: <BlogPostsPage />, index: true },
          { path: 'list', element: <BlogPostsPage /> },
          { path: ':title', element: <BlogPostPage /> },
          { path: ':title/edit', element: <BlogEditPostPage /> },
          { path: 'new', element: <BlogNewPostPage /> },
        ],
      },
      {
        path: 'courses',
        children: [
          { element: <CourseProfilePage />, index: true },
          { path: ':id/profile', element: <CourseProfilePage /> },
          { path: 'cards', element: <CourseCardsPage /> },
          { path: 'list', element: <CourseListPage /> },
          { path: 'new', element: <CourseCreatePage /> },
          { path: ':id/edit', element: <CourseEditPage /> },
          { path: 'configure', element: <CourseConfigurePage /> },
          { path: 'material', element: <CourseMaterialPage /> },
          { path: 'installment-calculator', element: <CourseInstallmentCalculatorPage /> },
          { path: 'account', element: <CourseAccountPage /> },
          { path: ':id/online', element: <CourseOnlineConfigurePage /> },
          { path: ':id/offline', element: <CourseOfflineConfigurePage /> },
          { path: ':id/hybrid', element: <CourseHybridConfigurePage /> },
          { path: 'group/list', element: <CourseGroupListPage /> },
          { path: 'group/:id/profile', element: <CourseGroupProfilePage /> },
          { path: 'logs', element: <CoursesLog /> },
          
        ],
      },
      {
        path: 'talent-hunt',
        children: [
          { element: <TalentHuntExamCreatePage />, index: true },
          { path: 'list', element: <TalentHuntExamCreatePage /> },
          { path: 'link-exams', element: <LinkTalentHuntExamsPage /> },
          { path: 'admit-card-instructions', element: <TalentHuntAdmitCardInstructionsPage /> },
          { path: ':id/profile', element: <TalentHuntExamProfilePage /> },
          { path: ':id/analytics', element: <HeightExamAnalyticsProfilePage /> },
          { path: 'result', element: <TalentHuntResultListPage /> },
          { path: 'students', element: <TalentHuntStudentsListPage /> },
          { path: ':id/group', element: <TalentHuntResultGroupProfilePage /> },
          { path: ':id/groupstudent', element: <TalentHuntResultGroupStudentProfilePage /> },
        ],
      },
      {
        path: 'heightexam',
        children: [
          { element: <HeightExamListPage />, index: true },
          { path: ':id/profile', element: <HeightExamProfilePage /> },
          { path: 'list', element: <HeightExamListPage /> },
          { path: 'new', element: <HeightExamCreatePage /> },
          { path: ':id/edit', element: <HeightExamEditPage /> },


        ],
      },
      {
        path: 'fee',
        children: [
          { element: <FeeProfilePage />, index: true },
          { path: 'profile', element: <FeeProfilePage /> },
          { path: 'cards', element: <FeeCardsPage /> },
          { path: 'list', element: <FeeListPage /> },
          { path: 'new', element: <FeeCreatePage /> },
          { path: ':id/edit', element: <FeeEditPage /> },
          { path: ':id/collect', element: <FeeCollectPage /> },

        ],
      },
      {
        path: 'chequeapproval',
        children: [
          { element: <ChequeApprovalListPage />, index: true },
          { path: 'list', element: <ChequeApprovalListPage /> },
        ],
      },
      {
        path: 'register',
        children: [
          { element: <RegisterListPage />, index: true },
          { path: 'list', element: <RegisterListPage /> },
          { path: ':id/profile', element: <RegisterProfilePage /> },
        ],
      },
      {
        path: 'job',
        children: [
          { element: <JobListPage />, index: true },
          { path: 'list', element: <JobListPage /> },
          { path: ':id', element: <JobDetailsPage /> },
          { path: 'new', element: <JobCreatePage /> },
          { path: ':id/edit', element: <JobEditPage /> },
        ],
      },
      {
        path: 'tour',
        children: [
          { element: <TourListPage />, index: true },
          { path: 'list', element: <TourListPage /> },
          { path: ':id', element: <TourDetailsPage /> },
          { path: 'new', element: <TourCreatePage /> },
          { path: ':id/edit', element: <TourEditPage /> },
        ],
      },
      {
        path: 'examination',
        children: [
          { element: <ExaminationListPage />, index: true },
          { path: ':id/profile', element: <ExaminationProfilePage /> },
          { path: ':id/rank', element: <ExaminationRankListPage /> },
          { path: 'list', element: <ExaminationListPage /> },
          { path: 'log', element: <ExamLogListPage /> },
          { path: 'new', element: <ExaminationCreatePage /> },
          { path: ':id/edit', element: <ExaminationEditPage /> },
        ],
      },

      {
        path: 'pattern',
        children: [
          { element: <PattrenListPage />, index: true },
          { path: 'list', element: <PattrenListPage /> },
          { path: 'new', element: <PattrenCreatePage /> },
          { path: ':id/edit', element: <PattrenEditPage /> },
          { path: ':id/duplicate', element: <PattrenDuplicatePage /> },
        ],
      },

      {
        path: 'attendanceadmin',
        children: [
          { element: <AttendanceAdminPage />, index: true },
          { path: 'list', element: <AttendanceAdminPage /> },
          { path: 'new', element: <AttendanceAdminCreatePage /> },
          { path: ':id/edit', element: <AttendanceAdminEditPage /> },
        ],
      },
      {
        path: 'questionbank',
        children: [
          { element: <QuestionBankListPage />, index: true },
          { path: 'list', element: <QuestionBankListPage /> },
          { path: 'new', element: <QuestionBankCreatePage /> },
          { path: 'logs', element: <QuestionBankLog /> },
          { path: ':id/edit', element: <QuestionBankEditPage /> },
          { path: ':id/profile', element: <QuestionBankProfilePage /> },
          { path: ':id/allocation', element: <QuestionBankAllocationPage /> },
        ],
      },
      {
        path: 'hostel',
        children: [
          { element: <HostelListPage />, index: true },
          { path: 'list', element: <HostelListPage /> },
          { path: 'campuslist', element: <HostelCampusListPage /> },
          { path: 'hostel-text', element: <HostelTextPage /> },
          { path: 'new', element: <HostelCreatePage /> },
          { path: 'logs', element: <HostelAllocationLog /> },
          { path: 'wardenlogs', element: <HostelWardenLog /> },
          { path: ':id/roomlist', element: <HostelListRoomPage /> },
          { path: 'hostellogs', element: <HostelLog /> },
          { path: 'allocation', element: <HostelAllocationPage /> },
          { path: 'allocation/list', element: <HostelAllocationListPage /> },
          { path: ':id/allocation', element: <HostelAllocationDetailPage /> },
          { path: ':id/agreement', element: <HostelAgreementPage /> },
          { path: ':id/allocation/outsider', element: <HostelAllocationOutsiderDetailPage /> },
          { path: ':id/edit', element: <HostelEditPage /> },
          { path: ':id/allocationedit', element: <HostelAllocationEditPage /> },
          { path: ':id/profile', element: <HostelProfilePage /> },
          // { path: 'collect', element: <HostelMessFeeCreatePage /> },
          { path: ':id/collect', element: <HostelMessFeeCreatePage /> },
          { path: 'vacated-beds', element: <HostelVacatedBedListPage /> },
          { path: 'vacate-refund', element: <HostelVacateRefundListPage /> },

        ],
      },
      {
        path: 'mess',
        children: [
          { path: ':id/collect', element: <HostelMessFeeCreatePage /> },
        ],
      },
      {
        path: 'testresultreport',
        children: [
          { element: <TestResultReportPage />, index: true },
          { path: 'list', element: <TestResultReportPage /> },
        ],
      },
      {
        path: 'quizsubjectmarksreport',
        children: [
          { element: <QuizSubjectMarksReportPage />, index: true },
          { path: 'list', element: <QuizSubjectMarksReportPage /> },
        ],
      },
      {
        path: 'queswiseanalysis',
        children: [
          { element: <QuesWiseAnalysisReportPage />, index: true },
          { path: 'list', element: <QuesWiseAnalysisReportPage /> },
        ],
      },
      {
        path: 'studenttestanalysis',
        children: [
          { element: <StudentTestAnalysisReportPage />, index: true },
          { path: 'list', element: <StudentTestAnalysisReportPage /> },
        ],
      },
      {
        path: 'studentqueswiseanalysis',
        children: [
          { element: <StudentQuesWiseAnalysisReportPage />, index: true },
          { path: 'list', element: <StudentQuesWiseAnalysisReportPage /> },
        ],
      },
      {
        path: 'studentreportcard',
        children: [
          { element: <StudentReportCardPage />, index: true },
          { path: 'list', element: <StudentReportCardPage /> },
        ],
      },
      {
        path: 'task',
        children: [
          { element: <TaskListPage />, index: true },
          { path: 'list', element: <TaskListPage /> },

        ],
      },
      {
        path: 'report',
        children: [
          { element: <CashRegisterReportPage />, index: true },
          { path: 'list', element: <CashRegisterReportPage /> },

        ],
      },
      {
        path: 'studentbalancereport',
        children: [
          { element: <StudentBalanceReportPage />, index: true },
          { path: 'list', element: <StudentBalanceReportPage /> },

        ],
      },
      {
        path: 'hostel-mess-balance-report',
        children: [
          { element: <HostelMessBalanceReportPage />, index: true },
          { path: 'list', element: <HostelMessBalanceReportPage /> },

        ],
      },
      {
        path: 'addmissionsummary',
        children: [
          { element: <AddmissionSummaryReportPage />, index: true },
          { path: 'list', element: <AddmissionSummaryReportPage /> },

        ],
      },
      {
        path: 'batchtopper',
        children: [
          { element: <BatchTopperReportPage />, index: true },
          { path: 'list', element: <BatchTopperReportPage /> },

        ],
      },
      {
        path: 'allpayments',
        children: [
          { element: <AllPaymentListPage />, index: true },
          { path: 'list', element: <AllPaymentListPage /> },
        ],
      },
      {
        path: 'syllabus',
        children: [
          { element: <SyllabusListPage />, index: true },
          { path: 'list', element: <SyllabusListPage /> },
          { path: 'new', element: <SyllabusCreatePage /> },
          { path: ':id/edit', element: <SyllabusEditPage /> },
          { path: ':id/profile', element: <SyllabusProfilePage /> },
          { path: 'logs', element: <SyllabusLog /> },
          
        ],
      },
      {
        path: 'refund',
        children: [
          { element: <RefundListPage />, index: true },
          { path: 'list', element: <RefundListPage /> },
          { path: 'new', element: <RefundCreatePage /> },
        ],
      },
      {
        path: 'offlineexam',
        children: [
          { element: <OfflineExamListPage />, index: true },
          { path: 'list', element: <OfflineExamListPage /> },
          { path: 'new', element: <OfflineExamCreatePage /> },
          { path: ':id/edit', element: <OfflineExamEditPage /> },
        ],
      },
      { path: 'permissions-configure', element: <NonAcademicPermissionDeniedPage /> },
      { path: 'roles', element: <RolePage /> },
      { path: 'dat', element: <DatConfigurationPage /> },
      { path: 'question-difficulty-configuration', element: <QuestionDifficultyConfigurationPage /> },
      { path: 'hostelmaster', element: <HostelMasterPage /> },
      { path: 'tax', element: <TaxPage /> },
      {
        path: 'center',
        children: [
          { element: <CenterPage />, index: true },
          { path: ':id/profile', element: <CenterProfilePage /> },
        ],
      },
      {
        path: 'school',
        children: [
          { element: <SchoolPage />, index: true },
          { path: ':id/profile', element: <SchoolProfilePage /> },
        ],
      },
      { path: 'tag', element: <TagPage /> },
      { path: 'organization', element: <OrganizationPage /> },
      { path: 'subject', element: <SubjectPage /> },
      { path: 'session', element: <SessionViewPage /> },
      { path: 'classes', element: <ClassesPage /> },
      { path: 'bank', element: <BankPage /> },
      { path: 'target', element: <TargetPage /> },
      { path: 'discount', element: <DiscountPage /> },
      { path: 'pattern', element: <PatternPage /> },
      { path: 'attendance', element: <AttendancePage /> },
      { path: 'feeparent', element: <ParentFeeListPage /> },
      { path: 'feestudent', element: <StudentFeeListPage /> },
      { path: 'file-manager', element: <FileManagerPage /> },
      { path: 'mail', element: <MailPage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'kanban', element: <KanbanPage /> },
      { path: 'permission', element: <PermissionDeniedPage /> },
      { path: 'blank', element: <BlankPage /> },
      { path: 'qr', element: <QRPage /> },
    ],
  },
];
